import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  Button,
  Chip,
  InputAdornment,
  Modal,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import ReusableTable from "../../common/table/CommonTable";
import {
  deleteSecurityOfficer,
  fetchSecurityOfficers,
} from "../../redux/secuityOfficer/securityAction";

const SecurityGuardList = () => {
  const [loading, setLoading] = useState(false);
  const [openDeleteModel, setOpenDeleteModel] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [deleteId, setDeleteId] = useState(null); // Changed from 0 to null
  const [searchTerm, setSearchTerm] = useState("");
  const [searchInputValue, setSearchInputValue] = useState("");
  const searchTimeoutRef = useRef(null);
  const rowsPerPage = 5;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const securityOfficerState = useSelector(
    (state) => state?.securityOfficer?.fetchSecurityOfficers || {}
  );
  const deleteSecurityOfficerState = useSelector(
    (state) => state.securityOfficer.deleteSecurityOfficer || {}
  );

  const securityOfficerList = securityOfficerState?.data?.data || [];
  const totalPages = securityOfficerState?.data?.totalPages || 0;

  useEffect(() => {
    const loadData = () => {
      const payload = {
        pageNo: currentPage,
        pagesize: rowsPerPage,
        pagination_required: true,
        search: searchTerm,
      };
      dispatch(fetchSecurityOfficers(payload));
    };
    loadData();
  }, [currentPage, searchTerm, dispatch]);

  const handleSearchChange = (event) => {
    const value = event.target.value;
    setSearchInputValue(value);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      setSearchTerm(value);
      setCurrentPage(1);
    }, 1000);
  };

  useEffect(() => {
    if (deleteSecurityOfficerState.success) {
      const payload = {
        pageNo: currentPage,
        pagesize: rowsPerPage,
        pagination_required: true,
        search: searchTerm,
      };
      dispatch(fetchSecurityOfficers(payload));
      // Reset delete state after successful deletion
      setDeleteId(null);
    }
  }, [deleteSecurityOfficerState.success, dispatch, currentPage, searchTerm]);

  const processedData = securityOfficerList.map((officer) => ({
    ...officer,
    blockName: officer.Block?.block_name || "N/A",
    floorName: officer.Floor?.floor_name || "N/A",
    wardName: officer.Ward?.ward_name || "N/A",
  }));

  // Filtering logic based on search term
  const lowercasedSearchTerm = searchTerm.toLowerCase();
  const filteredData = processedData.filter((officer) => {
    return (
      officer.security_serial_no.toString().includes(lowercasedSearchTerm) ||
      officer.security_gaurd_name
        .toLowerCase()
        .includes(lowercasedSearchTerm) ||
      officer.email.toLowerCase().includes(lowercasedSearchTerm) ||
      officer.phone_number.toString().includes(lowercasedSearchTerm) ||
      officer.blockName.toLowerCase().includes(lowercasedSearchTerm) ||
      officer.floorName.toLowerCase().includes(lowercasedSearchTerm) ||
      officer.wardName.toLowerCase().includes(lowercasedSearchTerm)
    );
  });

  const columns = [
    {
      id: "security_serial_no",
      label: "Guard ID",
      sortable: true,
      render: (value, row) => (
        <Typography
          component="span"
          sx={{
            cursor: "pointer",
            color: "dodgerblue",
            "&:hover": {
              textDecoration: "underline",
            },
          }}
          onClick={() => handleViewClick(row)}
        >
          {value}
        </Typography>
      ),
    },
    { id: "security_gaurd_name", label: "Name", sortable: true },
    { id: "email", label: "Email", sortable: true },
    { id: "phone_number", label: "Phone Number", sortable: true },
    { id: "blockName", label: "Block", sortable: true },
    { id: "floorName", label: "Floor", sortable: true },
    { id: "wardName", label: "Ward", sortable: true },
    {
      id: "status",
      label: "Status",
      sortable: true,
      render: (value) => (
        <Chip
          label={value === 1 ? "Active" : "Inactive"}
          color={value === 1 ? "success" : "warning"}
        />
      ),
    },
  ];

  const handleViewClick = (row) => {
    navigate(`/securityOfficer/view/${row.id}`);
  };

  const handleEditClick = (row) => {
    navigate(`/securityOfficer/edit/${row.id}`);
  };

  const handleDeleteClick = (row) => {
    setDeleteId(row.id);
    setOpenDeleteModel(true);
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleDeleteConfirm = () => {

    if (deleteId) {
      dispatch(deleteSecurityOfficer(deleteId));
      setOpenDeleteModel(false);
    } else {
      console.error("No delete ID available");
    }
  };

  const handleDeleteCancel = () => {
    setDeleteId(null);
    setOpenDeleteModel(false);
  };

  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  return (
    <Box>
      <Box mb={3}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search by name, email, ID, phone number, block, floor or ward..."
          value={searchInputValue}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {loading ? (
        <Typography>Loading...</Typography>
      ) : filteredData.length === 0 && searchTerm ? (
        <Typography>No records found</Typography>
      ) : (
        <ReusableTable
          title="Security Officers"
          columns={columns}
          data={filteredData}
          isLoading={securityOfficerState.inProgress}
          headerColor="#000"
          rowColor="#ffffff"
          alternateRowColor="#f5f5f5"
          onViewClick={handleViewClick}
          onEditClick={handleEditClick}
          onDeleteClick={handleDeleteClick}
          withPagination={true}
          withActions={true}
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
          showDownloadOption={true}
        />
      )}

      {/* Enhanced Delete Confirmation Modal */}
      <Modal
        open={openDeleteModel}
        style={{ left: "35%", top: "35%" }}
        onClose={handleDeleteCancel}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box
          sx={{
            width: "500px",
            background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
            padding: "30px",
            borderRadius: "12px",
            boxShadow: "0 10px 25px rgba(0,0,0,0.2)",
            border: "1px solid #e0e0e0",
          }}
        >
          <Typography
            id="modal-modal-title"
            variant="h5"
            sx={{
              fontSize: "1.4rem",
              fontWeight: "600",
              color: "#d32f2f",
              marginBottom: "10px",
              textAlign: "center",
            }}
            component="h2"
          >
            ⚠️ Delete Security Guard
          </Typography>
          <Typography
            id="modal-modal-description"
            sx={{
              mt: 2,
              mb: 3,
              fontSize: "1rem",
              color: "#424242",
              textAlign: "center",
              lineHeight: 1.6,
            }}
          >
            Are you sure you want to delete this security guard?
            <br />
            <strong>This action cannot be undone.</strong>
          </Typography>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              gap: 3,
              mt: 4,
            }}
          >
            <Button
              variant="contained"
              onClick={handleDeleteConfirm}
              sx={{
                backgroundColor: "#d32f2f",
                color: "white",
                padding: "10px 25px",
                borderRadius: "8px",
                fontWeight: "600",
                textTransform: "none",
                "&:hover": {
                  backgroundColor: "#b71c1c",
                  transform: "translateY(-2px)",
                  boxShadow: "0 4px 12px rgba(211,47,47,0.4)",
                },
                transition: "all 0.3s ease",
              }}
            >
              Delete
            </Button>
            <Button
              variant="outlined"
              onClick={handleDeleteCancel}
              sx={{
                borderColor: "#757575",
                color: "#757575",
                padding: "10px 25px",
                borderRadius: "8px",
                fontWeight: "600",
                textTransform: "none",
                "&:hover": {
                  borderColor: "#424242",
                  color: "#424242",
                  backgroundColor: "rgba(0,0,0,0.04)",
                  transform: "translateY(-2px)",
                },
                transition: "all 0.3s ease",
              }}
            >
              Cancel
            </Button>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

export default SecurityGuardList;
